import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TablePagination,
  IconButton,
  Tooltip,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  TextField,
  Collapse,
  Autocomplete,
} from '@mui/material';
import { Download, FilterList, Refresh } from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext.tsx';
import { apiService } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';

interface CarBrand {
  id: string;
  name: string;
  logo: string;
  image: string;
}

interface FileService {
  id: string;
  shortId?: string | null;
  status: 'PROCESSING' | 'COMPLETED' | 'CLOSED' | 'DECLINED';
  vehicleType?: string | null;
  carBrand?: string | null;
  carModel?: string | null;
  carEngine?: string | null;
  carPower?: string | null;
  carYear?: string | null;
  carGearbox?: string | null;
  carFuelType?: string | null;
  ecu?: string | null;
  vinCode?: string | null;
  readingTool?: string | null;
  customReadingTool?: string | null;
  readingMode?: string | null;
  hasNewMessage?: boolean | null;
  systemDescription?: string | null;
  creditsAmount?: number | null;
  originalFileUrl?: string | null;
  decodedOriginalFileUrl?: string | null;
  modifiedFileUrl?: string | null;
  memoryType?: string | null;
  isDecrypted?: boolean | null;
  createdAt: string;
  updatedAt?: string;
  solutions?: string[];
  autotunerEmail?: string | null;
  magicmotorsportsEmail?: string | null;
  customer?: {
    companyName?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    creditsAmount?: number | null;
    customerTypeId?: string | null;
    customerTypeName?: string | null;
    slaveSerialNumber?: string | null;
  } | null;
}

const FileServicePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const readingTools = [
    {
      id: 'MASTERTOOL',
      name: t('newFileService.readingTool.mastertool.name'),
      description: t('newFileService.readingTool.mastertool.description'),
    },
    {
      id: 'AUTOTUNER_SLAVE',
      name: t('newFileService.readingTool.autotunerSlave.name'),
      description: t('newFileService.readingTool.autotunerSlave.description'),
    },
    {
      id: 'FLEX_SLAVE',
      name: t('newFileService.readingTool.flexSlave.name'),
      description: t('newFileService.readingTool.flexSlave.description'),
    },
  ];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fileServices, setFileServices] = useState<FileService[]>([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<
    'ALL' | FileService['status']
  >('ALL');
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
  const [filterEcu, setFilterEcu] = useState<string>('');
  const [filterBrand, setFilterBrand] = useState<string>('');
  const [filterModel, setFilterModel] = useState<string>('');
  const [filterVin, setFilterVin] = useState<string>('');
  const [filterId, setFilterId] = useState<string>('');
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');
  // Applied filters (used for API) — updated only on Confirm
  const [appliedEcu, setAppliedEcu] = useState<string>('');
  const [appliedBrand, setAppliedBrand] = useState<string>('');
  const [appliedModel, setAppliedModel] = useState<string>('');
  const [appliedVin, setAppliedVin] = useState<string>('');
  const [appliedId, setAppliedId] = useState<string>('');
  const [appliedDateFrom, setAppliedDateFrom] = useState<string>('');
  const [appliedDateTo, setAppliedDateTo] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Brand filter select (like NewFileServicePage)
  const [carBrands, setCarBrands] = useState<CarBrand[]>([]);
  const [brandSearchInput, setBrandSearchInput] = useState<string>('');
  const [loadingCarBrands, setLoadingCarBrands] = useState<boolean>(false);
  const [selectedFilterBrand, setSelectedFilterBrand] = useState<CarBrand | null>(null);

  const formatDate = (dateString: string) =>
    format(new Date(dateString), 'dd-MM-yyyy HH:mm');

  const getStatusColor = (status: FileService['status']) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'PROCESSING':
        return 'warning';
      case 'CLOSED':
        return 'default';
      case 'DECLINED':
        return 'error';
      default:
        return 'default';
    }
  };

  // Load car brands for filter (debounced)
  useEffect(() => {
    const searchBrands = async () => {
      setLoadingCarBrands(true);
      try {
        const response = await apiService.getCarBrands(brandSearchInput.trim() || undefined);
        setCarBrands(response.items);
      } catch (err) {
        console.error('Error loading car brands:', err);
        setCarBrands([]);
      } finally {
        setLoadingCarBrands(false);
      }
    };
    const timeoutId = setTimeout(searchBrands, 300);
    return () => clearTimeout(timeoutId);
  }, [brandSearchInput]);

  useEffect(() => {
    const loadFiles = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getFiles({
          page: page + 1,
          limit: rowsPerPage,
          status: statusFilter === 'ALL' ? undefined : statusFilter,
          ecu: appliedEcu.trim() || undefined,
          carBrandId: appliedBrand.trim() || undefined,
          carModel: appliedModel.trim() || undefined,
          vinCode: appliedVin.trim() || undefined,
          shortExternalId: appliedId.trim() || undefined,
          dateFrom: appliedDateFrom || undefined,
          dateTo: appliedDateTo || undefined,
        });
        setFileServices(response.files);
        setTotal(response.total);
      } catch (err: any) {
        console.error('Error loading files:', err);
        setError(err.message || t('fileService.errors.failedToLoad'));
        setFileServices([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };

    loadFiles();
  }, [page, rowsPerPage, statusFilter, appliedEcu, appliedBrand, appliedModel, appliedVin, appliedId, appliedDateFrom, appliedDateTo, refreshKey]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    // eslint-disable-next-line no-undef
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleView = (fileServiceId: string) => {
    navigate(`/file-service/${fileServiceId}`);
  };

  const handleFiltersConfirm = () => {
    setAppliedEcu(filterEcu);
    setAppliedBrand(filterBrand);
    setAppliedModel(filterModel);
    setAppliedVin(filterVin);
    setAppliedId(filterId);
    setAppliedDateFrom(filterDateFrom);
    setAppliedDateTo(filterDateTo);
    setPage(0);
  };

  const handleFiltersReset = () => {
    setFilterEcu('');
    setFilterBrand('');
    setFilterModel('');
    setFilterVin('');
    setFilterId('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setAppliedEcu('');
    setAppliedBrand('');
    setAppliedModel('');
    setAppliedVin('');
    setAppliedId('');
    setAppliedDateFrom('');
    setAppliedDateTo('');
    setSelectedFilterBrand(null);
    setBrandSearchInput('');
    setPage(0);
  };

  const handleOpenFile = (url?: string | null) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        minWidth: 0,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('fileService.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('fileService.subtitle')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => setRefreshKey(k => k + 1)}
            disabled={loading}
          >
            {t('fileService.refresh')}
          </Button>
          {user?.userType === 'customer' && (
            <Button
              variant="contained"
              onClick={() => navigate('/new-file-service')}
            >
              {t('fileService.addFileService')}
            </Button>
          )}
        </Stack>
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
        sx={{ mb: 2 }}
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t('fileService.status.label')}</InputLabel>
          <Select
            label={t('fileService.status.label')}
            value={statusFilter}
            onChange={event => {
              setStatusFilter(
                event.target.value as 'ALL' | FileService['status'],
              );
              setPage(0);
            }}
          >
            <MenuItem value="ALL">{t('fileService.status.all')}</MenuItem>
            <MenuItem value="PROCESSING">
              {t('fileService.status.processing')}
            </MenuItem>
            <MenuItem value="COMPLETED">
              {t('fileService.status.completed')}
            </MenuItem>
            <MenuItem value="CLOSED">{t('fileService.status.closed')}</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant={filtersOpen ? 'contained' : 'outlined'}
          startIcon={<FilterList />}
          onClick={() => setFiltersOpen(prev => !prev)}
        >
          {t('fileService.filters.button')}
        </Button>
      </Stack>

      <Collapse in={filtersOpen}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
            {t('fileService.filters.title')}
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <TextField
                size="small"
                label={t('fileService.filters.id')}
                value={filterId}
                onChange={e => setFilterId(e.target.value)}
                sx={{ minWidth: 140 }}
              />
              <TextField
                size="small"
                label={t('fileService.filters.ecu')}
                value={filterEcu}
                onChange={e => setFilterEcu(e.target.value)}
                sx={{ minWidth: 140 }}
              />
              <Autocomplete
                size="small"
                sx={{ minWidth: 140 }}
                options={carBrands}
                getOptionLabel={option => option.name}
                loading={loadingCarBrands}
                value={selectedFilterBrand}
                inputValue={brandSearchInput}
                onInputChange={(_event, newInputValue) => setBrandSearchInput(newInputValue)}
                onChange={(_event, newValue) => {
                  setSelectedFilterBrand(newValue);
                  setFilterBrand(newValue ? newValue.id : '');
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('fileService.filters.brand')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCarBrands ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <TextField
                size="small"
                label={t('fileService.filters.model')}
                value={filterModel}
                onChange={e => setFilterModel(e.target.value)}
                sx={{ minWidth: 140 }}
              />
              <TextField
                size="small"
                label={t('fileService.filters.vin')}
                value={filterVin}
                onChange={e => setFilterVin(e.target.value)}
                sx={{ minWidth: 140 }}
              />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                size="small"
                label={t('fileService.filters.dateFrom')}
                type="date"
                value={filterDateFrom}
                onChange={e => setFilterDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 160 }}
              />
              <TextField
                size="small"
                label={t('fileService.filters.dateTo')}
                type="date"
                value={filterDateTo}
                onChange={e => setFilterDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 160 }}
              />
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="contained" size="small" onClick={handleFiltersConfirm}>
              {t('fileService.filters.confirm')}
            </Button>
            <Button variant="outlined" size="small" onClick={handleFiltersReset}>
              {t('fileService.filters.reset')}
            </Button>
          </Stack>
        </Paper>
      </Collapse>
      {error && (
        <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ width: '100%', overflowX: 'auto', minWidth: 0 }}>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="h6">
                    {t('fileService.table.id')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="h6">
                    {t('fileService.table.vehicle')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="h6">
                    {t('fileService.table.engine')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="h6">
                    {t('fileService.table.vin')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="h6">
                    {t('fileService.table.readingTool')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="h6">
                    {t('fileService.table.credits')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="h6">
                    {t('fileService.table.solutions')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="h6">
                    {t('fileService.table.ecu')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="h6">
                    {t('fileService.table.created')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="h6">
                    {t('fileService.table.status')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="h6">
                    {t('fileService.table.files')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="h6"></Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fileServices.map(fileService => (
                <TableRow key={fileService.id}>
                  <TableCell>{fileService.shortId}</TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" fontWeight={600}>
                        {fileService.carBrand || '—'}{' '}
                        {fileService.carModel || ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fileService.vehicleType || '—'}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <Typography variant="body2">
                        {fileService.carEngine || '—'}
                        {fileService.carPower
                          ? ` · ${fileService.carPower}`
                          : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[
                          fileService.carYear,
                          fileService.carGearbox,
                          fileService.carFuelType,
                        ]
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{fileService.vinCode || '—'}</TableCell>
                  <TableCell>
                    {readingTools.find(
                      tool => tool.id === fileService.readingTool,
                    )?.name || '—'}
                  </TableCell>
                  <TableCell>
                    {typeof fileService.creditsAmount === 'number'
                      ? fileService.creditsAmount.toFixed(0)
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {fileService.solutions &&
                    fileService.solutions.length > 0 ? (
                      <Stack spacing={0.25}>
                        {fileService.solutions.map(solution => (
                          <Chip
                            key={solution}
                            label={solution}
                            size="small"
                            color="secondary"
                            variant="filled"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        ))}
                      </Stack>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{fileService.ecu || '—'}</TableCell>
                  <TableCell>{formatDate(fileService.createdAt)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Chip
                        label={t(
                          `fileService.status.${fileService.status.toLowerCase()}`,
                        )}
                        size="small"
                        color={getStatusColor(fileService.status)}
                        variant="outlined"
                      />
                      {fileService.hasNewMessage && (
                        <Chip
                          label={t('fileService.activeDialog')}
                          size="small"
                          color="info"
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title={t('fileService.tooltips.originalFile')}>
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={!fileService.originalFileUrl}
                            onClick={() =>
                              handleOpenFile(fileService.originalFileUrl)
                            }
                          >
                            <Download fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {user?.userType === 'admin' &&
                        fileService.decodedOriginalFileUrl && (
                          <Tooltip
                            title={t(
                              'fileService.tooltips.decodedOriginalFile',
                            )}
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  handleOpenFile(
                                    fileService.decodedOriginalFileUrl,
                                  )
                                }
                              >
                                <Download fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      <Tooltip title={t('fileService.tooltips.modifiedFile')}>
                        <span>
                          <IconButton
                            size="small"
                            color="secondary"
                            disabled={!fileService.modifiedFileUrl}
                            onClick={() =>
                              handleOpenFile(fileService.modifiedFileUrl)
                            }
                          >
                            <Download fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleView(fileService.id)}
                    >
                      {t('fileService.view')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && fileServices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {t('fileService.noFileServicesFound')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {loading && (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default FileServicePage;
